import { faCheck, faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, type TBadgeType } from 'components/badge/Badge';
import { ButtonNew } from 'components/buttonNew';
import { t } from 'i18next';
import React, { useState } from 'react';

export interface IAccordionItem {
  success_badge: boolean;
  badge_type?: TBadgeType;
  success_preview?: React.JSX.Element;
  badge?: string;
  title: string;
  content?: React.JSX.Element;
  onSave?: () => boolean | Promise<boolean>;
  onOpen?: () => void;
  onCloseWithoutSave?: () => void;
  expandable?: boolean; // undefined = expanadable
  action?: React.JSX.Element;
}

interface IPageProps {
  items: IAccordionItem[];
}

interface IState {
  openItems: number[];
}

export default function Accordion({ items }: IPageProps) {
  const [state, setState] = useState<IState>({
    openItems: [],
  });

  function closeItem(i: number, withoutSave: boolean) {
    if (withoutSave) {
      items[i]?.onCloseWithoutSave?.();
    }

    setState(prev => ({
      openItems: prev.openItems.filter(openIndex => openIndex !== i),
    }));
  }

  function openItem(i: number) {
    setState(prev => {
      const previouslyOpen = prev.openItems.find(openIndex => openIndex !== i);
      if (previouslyOpen != null) {
        items[previouslyOpen]?.onCloseWithoutSave?.();
      }

      items[i]?.onOpen?.();
      return { openItems: [i] };
    });
  }

  function toggleItem(item: IAccordionItem, i: number) {
    if (state.openItems.includes(i)) {
      closeItem(i, true);
    } else {
      openItem(i);
    }
  }

  async function handleSave(item: IAccordionItem, i: number) {
    if (item.onSave) {
      const successful = await item.onSave();
      if (successful) {
        closeItem(i, false);
      }
    }
  }

  return <div className="mt-2">
    {items.map((item, i) => {
      const expandable = item?.expandable === true;
      const isOpen = expandable && state.openItems.includes(i);
      const badgeType = item.badge_type ?? (item.success_badge ? 'success' : 'gray');

      return <div className="py-4 px-6 border-b border-l border-r border-gray-200 first:rounded-t-2xl first:border-t last:rounded-b-2xl " key={item.title}>
        <div
          className={`flex justify-between items-center${expandable ? ' cursor-pointer' : ''}`}
          onClick={expandable ? () => toggleItem(item, i) : undefined}
        >
          <div className="flex flex-col gap-2">
            {item.badge !== undefined &&
              <div>
                <Badge type={badgeType} className="shrink" outline={true}>
                  {item.badge}
                  {badgeType === 'success' && <FontAwesomeIcon icon={faCheck} className="pl-1" />}
                </Badge>
              </div>}
            {item.title}
            {(item.success_badge || item.expandable === false) && item.success_preview}
          </div>
          {expandable ? (
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
          ) : (
            item.action
          )}
        </div>
        {isOpen && item.content != null &&
          <div className="border-t border-gray-200 mt-4 pt-4">
            {item.content}
            <div className="flex gap-2 justify-end mt-4">
              <ButtonNew type="tertiary" onClick={() => closeItem(i, true)}>{t('Cancel')}</ButtonNew>
              <ButtonNew type="primary" onClick={() => void handleSave(item, i)}>{t('Save')}</ButtonNew>
            </div>
          </div>
        }
      </div>;
    })}
  </div>;
}
